/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { jsx, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ButtonItem from '@atlaskit/menu/button-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { RemixHiddenOptionKind } from '../../../types';

import { SUB_TYPE_ICON_MAP } from './constants';
import type { KnownSubType, RemixOption, RemixOptionKind, RemixSubType } from './types';
import { getRemixMenuOptions } from './utils';

export type CascadeFlyoutDirection = 'left' | 'right';

// Width of a menu column (top-level categories and sub-type submenu).
const CATEGORY_WIDTH = 200;
const SUBTYPE_WIDTH = 160;
const PREVIEW_WIDTH = 176;
const CASCADE_VIEWPORT_MARGIN = 8;
const CASCADE_FLYOUT_WIDTH = SUBTYPE_WIDTH + PREVIEW_WIDTH;

export function getCascadeDirectionForAnchor(
	anchorRect: DOMRect,
	viewportWidth: number,
): CascadeFlyoutDirection {
	const fullCascadeWidth = CATEGORY_WIDTH + CASCADE_FLYOUT_WIDTH;
	const availableRightForFullCascade = viewportWidth - anchorRect.left - CASCADE_VIEWPORT_MARGIN;
	const availableLeftForFlyout = anchorRect.left - CASCADE_VIEWPORT_MARGIN;

	return availableRightForFullCascade < fullCascadeWidth &&
		availableLeftForFlyout >= CASCADE_FLYOUT_WIDTH
		? 'left'
		: 'right';
}

const subtleSlideIn = keyframes({
	from: { opacity: 0, transform: 'translateX(-8px)' },
	to: { opacity: 1, transform: 'translateX(0)' },
});

const menuStyles = cssMap({
	popupEnterAnimation: {
		animationName: subtleSlideIn,
		animationDuration: '200ms',
		animationTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
		animationFillMode: 'backwards',
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
		},
	},
	// Keep every layer in normal flow so ADS Popup measures/renders the whole cascade.
	// In left mode, Popup uses bottom-end placement and row-reverse keeps the category
	// column by the trigger while subtype/preview columns appear to its left.
	rootRight: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'stretch',
	},
	rootLeft: {
		display: 'flex',
		flexDirection: 'row-reverse',
		alignItems: 'stretch',
	},
	categoryColumn: {
		width: `${CATEGORY_WIDTH}px`,
		flex: '0 0 auto',
		display: 'flex',
		flexDirection: 'column',
	},
	// Grows to fill the column so the footer is pushed to the bottom edge, keeping the
	// describe input anchored regardless of how tall the adjacent subtype column is.
	categoryMenu: {
		flexGrow: 1,
	},
	subTypeColumnRight: {
		width: `${SUBTYPE_WIDTH}px`,
		flex: '0 0 auto',
		borderInlineStartWidth: token('border.width'),
		borderInlineStartStyle: 'solid',
		borderInlineStartColor: token('color.border'),
	},
	subTypeColumnLeft: {
		width: `${SUBTYPE_WIDTH}px`,
		flex: '0 0 auto',
		borderInlineEndWidth: token('border.width'),
		borderInlineEndStyle: 'solid',
		borderInlineEndColor: token('color.border'),
	},
	previewColumnRight: {
		width: `${PREVIEW_WIDTH}px`,
		flex: '0 0 auto',
		alignSelf: 'center',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		boxSizing: 'border-box',
		borderInlineStartWidth: token('border.width'),
		borderInlineStartStyle: 'solid',
		borderInlineStartColor: token('color.border'),
	},
	previewColumnLeft: {
		width: `${PREVIEW_WIDTH}px`,
		flex: '0 0 auto',
		alignSelf: 'center',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		boxSizing: 'border-box',
		borderInlineEndWidth: token('border.width'),
		borderInlineEndStyle: 'solid',
		borderInlineEndColor: token('color.border'),
	},
	// Trailing chevron for a category row that opens a submenu.
	categoryTrailing: {
		display: 'flex',
		alignItems: 'center',
	},
	previewContent: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		pointerEvents: 'none',
	},
});

interface RemixSubTypeColumnProps {
	subTypes: RemixSubType[];
	direction: CascadeFlyoutDirection;
	onSubTypeSelect: (subTypeValue: string) => void;
	onHoverSubType: (subTypeValue: string | null) => void;
}

function RemixSubTypeColumn({
	subTypes,
	direction,
	onSubTypeSelect,
	onHoverSubType,
}: RemixSubTypeColumnProps) {
	const { formatMessage } = useIntl();

	return (
		<Box
			xcss={direction === 'left' ? menuStyles.subTypeColumnLeft : menuStyles.subTypeColumnRight}
			testId="remix-menu-submenu"
		>
			<Box xcss={menuStyles.popupEnterAnimation}>
				<MenuGroup>
					<Section>
						{subTypes.map((subType) => {
							const label = formatMessage(subType.labelMessage);
							return (
								<Box
									key={subType.value}
									onMouseEnter={() => onHoverSubType(subType.value)}
									onFocus={() => onHoverSubType(subType.value)}
								>
									<ButtonItem
										testId={subType.testId ?? `remix-menu-subtype-${subType.value}`}
										onClick={() => onSubTypeSelect(subType.value)}
									>
										{label}
									</ButtonItem>
								</Box>
							);
						})}
					</Section>
				</MenuGroup>
			</Box>
		</Box>
	);
}

function RemixPreviewColumn({
	subType,
	direction,
}: {
	subType: RemixSubType | null;
	direction: CascadeFlyoutDirection;
}) {
	const { formatMessage } = useIntl();
	const IconComponent =
		subType && subType.value in SUB_TYPE_ICON_MAP
			? SUB_TYPE_ICON_MAP[subType.value as KnownSubType]
			: null;

	if (!IconComponent || !subType) {
		return null;
	}

	const label = formatMessage(subType.labelMessage);

	return (
		<Box xcss={direction === 'left' ? menuStyles.previewColumnLeft : menuStyles.previewColumnRight}>
			<Box xcss={menuStyles.previewContent} testId="remix-menu-preview">
				<IconComponent alt={label} />
			</Box>
		</Box>
	);
}

interface RemixCategoryItemProps {
	option: RemixOption;
	isActive: boolean;
	onActivate: (option: RemixOption) => void;
	onFlatSelect: (type: RemixOptionKind) => void;
	renderOptionIcon: (option: RemixOption, alt: string) => JSX.Element;
}

function RemixCategoryItem({
	option,
	isActive,
	onActivate,
	onFlatSelect,
	renderOptionIcon,
}: RemixCategoryItemProps) {
	const { formatMessage } = useIntl();

	const label = formatMessage(option.labelMessage);
	const optionIconAlt = formatMessage(option.iconAltMessage);
	const hasSubTypes = option.subTypes.length > 0;

	if (!hasSubTypes) {
		return (
			<Box onMouseEnter={() => onActivate(option)} onFocus={() => onActivate(option)}>
				<ButtonItem
					iconBefore={renderOptionIcon(option, optionIconAlt)}
					testId={option.testId}
					onClick={() => onFlatSelect(option.type)}
				>
					{label}
				</ButtonItem>
			</Box>
		);
	}

	return (
		<Box onMouseEnter={() => onActivate(option)} onFocus={() => onActivate(option)}>
			<ButtonItem
				isSelected={isActive}
				iconBefore={renderOptionIcon(option, optionIconAlt)}
				iconAfter={
					<Box xcss={menuStyles.categoryTrailing}>
						<ChevronRightIcon label="" color={token('color.icon')} size="small" />
					</Box>
				}
				testId={option.testId}
				onClick={() => onActivate(option)}
			>
				{label}
			</ButtonItem>
		</Box>
	);
}

export interface RemixCascadingMenuContentProps {
	onOptionSelect?: (parameters: {
		type: RemixOptionKind;
		subType?: string;
		userPrompt?: string;
	}) => void;
	options?: RemixOption[];
	hiddenOptions?: readonly RemixHiddenOptionKind[];
	flyoutDirection?: CascadeFlyoutDirection;
	showFlyout?: boolean;
	renderOptionIcon: (option: RemixOption, alt: string) => JSX.Element;
	/** Rendered below the category list, e.g. the free-text prompt row. */
	footer?: ReactNode;
}

/**
 * RemixCascadingMenuContent — lightweight cascading dropdown menu (like a toolbar /
 * right-click menu): top-level categories (Charts, Diagrams, …) each open a right-side
 * submenu of sub-types (Line, Area, …). Selecting a sub-type emits `{ type, subType }`.
 * Categories without sub-types are directly clickable and emit only `{ type }`, letting
 * the backend choose.
 *
 * This is the "inline dropdown" Remix menu variant, used by RemixPopup (anchored popup
 * presentation) as a lighter-weight alternative to RemixOverlayContent's accordion.
 */
export function RemixCascadingMenuContent({
	onOptionSelect,
	options,
	hiddenOptions,
	flyoutDirection = 'right',
	showFlyout = true,
	renderOptionIcon,
	footer,
}: RemixCascadingMenuContentProps): JSX.Element {
	const resolvedOptions = useMemo(
		() =>
			getRemixMenuOptions({
				source:
					options === undefined
						? { kind: 'preset', preset: 'selection' }
						: {
								kind: 'custom',
								choices: options,
							},
				excludedRemixTypes: hiddenOptions ? [...hiddenOptions] : undefined,
			}),
		[hiddenOptions, options],
	);

	// The category whose sub-types are currently shown in the second column. Only
	// categories that have sub-types open a flyout; the rest close it.
	const [activeType, setActiveType] = useState<RemixOptionKind | null>(null);
	// The sub-type currently hovered/focused, driving the preview column.
	const [hoveredSubType, setHoveredSubType] = useState<string | null>(null);

	const activeOption = useMemo(
		() => resolvedOptions.find((opt) => opt.type === activeType && opt.subTypes.length > 0) ?? null,
		[resolvedOptions, activeType],
	);
	const activeSubTypes = activeOption?.subTypes ?? [];
	const hoveredSubTypeDef = activeSubTypes.find((st) => st.value === hoveredSubType) ?? null;
	const handleActivate = useCallback((option: RemixOption) => {
		setActiveType(option.subTypes.length > 0 ? option.type : null);
		setHoveredSubType(null);
	}, []);

	const handleFlatSelect = useCallback(
		(type: RemixOptionKind) => {
			onOptionSelect?.({ type });
		},
		[onOptionSelect],
	);

	const handleSubTypeSelect = useCallback(
		(subTypeValue: string) => {
			if (!activeOption) {
				return;
			}
			if (fg('cc-maui-phase-3')) {
				const subTypeDef = activeOption.subTypes.find((st) => st.value === subTypeValue);
				onOptionSelect?.({
					type: subTypeDef?.emitType ?? activeOption.type,
					subType: subTypeValue,
				});
				return;
			}
			onOptionSelect?.({ type: activeOption.type, subType: subTypeValue });
		},
		[activeOption, onOptionSelect],
	);

	return (
		<Box
			xcss={flyoutDirection === 'left' ? menuStyles.rootLeft : menuStyles.rootRight}
			testId="remix-menu"
		>
			<Box xcss={menuStyles.categoryColumn}>
				<Box xcss={menuStyles.categoryMenu}>
					<MenuGroup>
						<Section>
							{resolvedOptions.map((option) => (
								<RemixCategoryItem
									key={option.type}
									option={option}
									isActive={activeType === option.type}
									onActivate={handleActivate}
									onFlatSelect={handleFlatSelect}
									renderOptionIcon={renderOptionIcon}
								/>
							))}
						</Section>
					</MenuGroup>
				</Box>
				{footer}
			</Box>
			{showFlyout && activeSubTypes.length > 0 ? (
				<RemixSubTypeColumn
					key={activeType}
					subTypes={activeSubTypes}
					direction={flyoutDirection}
					onSubTypeSelect={handleSubTypeSelect}
					onHoverSubType={setHoveredSubType}
				/>
			) : null}
			{showFlyout && hoveredSubTypeDef ? (
				<RemixPreviewColumn subType={hoveredSubTypeDef} direction={flyoutDirection} />
			) : null}
		</Box>
	);
}
